import { fg } from '@atlaskit/platform-feature-flags/fg';
import { IdentityUserAri } from '@atlassian/ari/identity';
import { type EntityATI } from '@atlassian/search-client/common/types';
import { type ThirdPartyFilters } from '@atlassian/search-client/query-generator';

import { type TypeFilterValueKey } from '../../../common/constants/filters/universal-type';
import { is3pProductKey, ProductKeys } from '../../../common/constants/product-keys';
import {
	type ThirdPartyConfigsBootstrap,
	type ThirdPartyConfigsWithAPIResponse,
} from '../../../common/constants/schemas/3p-config';
import type { SearchStateFilters, SearchStoreExperience } from '../../../common/constants/search';
import { SearchExperience } from '../../../common/constants/search-experience';
import { type SelectedFiltersType } from '../../../common/constants/types';
import { getThirdPartyFilters } from '../../../common/utils/filters';
import { getProducts } from '../../../common/utils/get-products';
import { isAllProductSearch } from '../../../common/utils/is-all-product-search';
import { resolveSalesforceCloseDateForQuery } from '../../../common/utils/salesforce-close-date';
import {
	archivedAndPendingComponentsFilterId,
	archivedFilterId,
	assetsSchemaFilterId,
	assetsTypeFilterId,
	assigneeFilterId,
	campaignStatusFilterId,
	caseStatusFilterId,
	documentStatusFilterId,
	calendarTypeFilterId,
	leadStatusFilterId,
	taskStatusFilterId,
	taskTypeFilterId,
	closeDateFilterId,
	commentedByMeFilterId,
	contentFilterId,
	contributorFilterId,
	createdByFilterId,
	focusAreaTypeFilterId,
	focusAreaUnderFilterId,
	hasAttachmentFilterId,
	labelFilterId,
	lastUpdatedByFilterId,
	lastUpdatedFilterId,
	opportunityStatusFilterId,
	ownerFilterId,
	pullRequestStatusFilterId,
	repositoryFilterId,
	reviewerFilterId,
	pageStatusFilterId,
	positionEmploymentTypeFilterId,
	positionFocusAreaFilterId,
	positionJobFamilyFilterId,
	positionStatusFilterId,
	positionTeamFilterId,
	projectFilterId,
	reporterFilterId,
	salesforceStageFilterId,
	slackChannelFilterId,
	workspaceNameFilterId,
	sortFilterId,
	spaceFilterId,
	statusFilterId,
	titleMatchFilterId,
	verifiedFilterId,
	watchedFilterId,
} from '../../store/search/filters/filter-ids';
import { getTypeEntities } from '../../store/search/get-type-entities';

import {
	getArchivedAndPendingComponentsValue,
	getBooleanFilterValue,
	getContainerStatusValue,
	getContentStatusValue,
	getFilterDateRange,
	getJiraIssueStatusFilterOptions,
	getJiraProjectFilter,
	getSelectedDateRange,
	getSelectedJiraWorkType,
	getSelectFilterValue,
	getSortFilters,
	omitNonJiraFilterValues,
} from './utils';

/** The shape returned by getSelectedFilters — contains all filter values grouped by product/scope. */
export type SelectedFiltersReturnType = {
	selectedFilters: SelectedFiltersType;
	unselectedProductsThirdPartyFilters: ThirdPartyFilters;
	hasFiltersSelected: boolean;
	selectedEntities: string[];
	entitiesForResultsCountQuery: EntityATI[];
	entities: EntityATI[];
};

type GetSelectedFiltersPropsFromBootstrap = {
	availableProducts: ProductKeys[];
	allFilterExcludedProducts: ProductKeys[];
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	currentUserId?: string | null;
};

type GetSelectedFiltersPropsFromSearch = {
	filters: SearchStateFilters;
	selectedProducts: ProductKeys[];
	selectedDatasource?: ThirdPartyConfigsWithAPIResponse;
	jsmIncidentFilterIds: string[];
	experience?: SearchStoreExperience;
};

type GetSelectedFiltersProps = GetSelectedFiltersPropsFromBootstrap &
	GetSelectedFiltersPropsFromSearch;

/**
 * Derives the selected filter values (spaces, contributors, types, third-party ARIs, etc.)
 * from the current filter state, product selection, and search configuration.
 *
 * Maps UI filter selections into query parameters like spacesFilter,
 * contributorAccountIds, containerAris, and ancestorAris.
 *
 * Pure function — safe for SSR and non-React contexts.
 */
export const getSelectedFilters = ({
	filters,
	selectedProducts,
	selectedDatasource,
	availableProducts: products,
	allFilterExcludedProducts,
	thirdPartyConfigs,
	currentUserId,
	jsmIncidentFilterIds,
	experience = SearchExperience.SEARCH_PAGE,
}: GetSelectedFiltersProps): SelectedFiltersReturnType => {
	const { selectedProduct, filteredProducts, availableProducts } = getProducts({
		selectedProducts,
		availableProducts: products,
	});
	const { selectedEntities, entitiesForQuery, entitiesForResultsCountQuery, selectedTypes } =
		getTypeEntities(
			{ availableProducts, filters, selectedProducts, allFilterExcludedProducts },
			{
				thirdPartyConfigs,
			},
		);

	const watchedByMeValue = getBooleanFilterValue(filters[watchedFilterId]);
	const titleMatchValue = getBooleanFilterValue(filters[titleMatchFilterId]);
	const selectedDateRange = getSelectedDateRange(filters[lastUpdatedFilterId]);
	const filterDateRange = getFilterDateRange(filters[lastUpdatedFilterId]);
	const commonContributors = getSelectFilterValue(filters[contributorFilterId]).length
		? getSelectFilterValue(filters[contributorFilterId])
		: undefined;
	const pageStatusValue = getSelectFilterValue(filters[pageStatusFilterId])[0];

	const assigneeARIValues = getSelectFilterValue(filters[assigneeFilterId]).map((assigneeId) => {
		return `ari:cloud:identity::user/${assigneeId}`;
	});
	const reporterARIValues = getSelectFilterValue(filters[reporterFilterId]).map((reporterId) => {
		return `ari:cloud:identity::user/${reporterId}`;
	});

	const watcherARIValues = (watchedByMeValue && currentUserId ? [currentUserId] : []).map(
		(watcherId) => {
			return `ari:cloud:identity::user/${watcherId}`;
		},
	);

	const commenterARIValues =
		getBooleanFilterValue(filters[commentedByMeFilterId]) && currentUserId
			? [`ari:cloud:identity::user/${currentUserId}`]
			: [];

	const assignee3PValues = getSelectFilterValue(filters[assigneeFilterId]).map((assigneeId) => {
		if (assigneeId.startsWith('ari:cloud:')) {
			return IdentityUserAri.parse(assigneeId).resourceId;
		}
		return assigneeId;
	});

	const isSelectedThirdPartyProduct = !!selectedProduct && is3pProductKey(selectedProduct);

	const selectedSpaceValues = getSelectFilterValue(filters[spaceFilterId]);
	const selectedContentValues = getSelectFilterValue(filters[contentFilterId]);

	const confluenceOnlyValues = <T>(values: T[]): T[] => (isSelectedThirdPartyProduct ? [] : values);
	const thirdPartySpaceValues =
		selectedProduct === ProductKeys.GoogleDrive || selectedProduct === ProductKeys.Sharepoint
			? selectedSpaceValues
			: [];
	const thirdPartyContentValues = isSelectedThirdPartyProduct ? selectedContentValues : [];
	const containerAris = [...getSelectFilterValue(filters[projectFilterId])];

	const createdByValues = getSelectFilterValue(filters[createdByFilterId]);
	const labelsValues = getSelectFilterValue(filters[labelFilterId]);
	const lastUpdatedByValues = getSelectFilterValue(filters[lastUpdatedByFilterId]);
	// When both the primary (space) and dependent (contentUnder) filters are selected, the backend
	// treats ancestorAris with OR logic, which would incorrectly broaden results.
	// Prefer the dependent filter when present since it is more specific; otherwise fall back to
	// the primary filter. The store handles the reverse: when the primary filter is selected after
	// the dependent, it clears the dependent so only the primary is sent.
	const ancestorAris =
		thirdPartyContentValues.length > 0 ? thirdPartyContentValues : thirdPartySpaceValues;
	const ownerValues = getSelectFilterValue(filters[ownerFilterId]);
	const opportunityStatusValue = getSelectFilterValue(filters[opportunityStatusFilterId])[0];
	const pullRequestStatusValues = getSelectFilterValue(filters[pullRequestStatusFilterId]);
	const repositoryValues = getSelectFilterValue(filters[repositoryFilterId]);
	const reviewerValues = getSelectFilterValue(filters[reviewerFilterId]);
	const campaignStatusValues = getSelectFilterValue(filters[campaignStatusFilterId]);
	const caseStatusValues = getSelectFilterValue(filters[caseStatusFilterId]);
	const documentStatusValues = getSelectFilterValue(filters[documentStatusFilterId]);
	const calendarTypeValues = getSelectFilterValue(filters[calendarTypeFilterId]);
	const leadStatusValues = getSelectFilterValue(filters[leadStatusFilterId]);
	const taskStatusValues = getSelectFilterValue(filters[taskStatusFilterId]);
	const taskTypeValues = getSelectFilterValue(filters[taskTypeFilterId]);
	const salesforceStageValues = getSelectFilterValue(filters[salesforceStageFilterId]);
	const slackChannelValues = getSelectFilterValue(filters[slackChannelFilterId]);
	const workspaceNameValues = fg('rovo_fps_show_workspace_name_filter')
		? getSelectFilterValue(filters[workspaceNameFilterId])
		: [];
	const hasAttachmentValue = getBooleanFilterValue(filters[hasAttachmentFilterId]);

	const sortFilters = getSortFilters(filters[sortFilterId]);

	// Get close date value for Salesforce filter
	const closeDateFilterState = filters[closeDateFilterId];
	const closeDateSelectedOption = getSelectFilterValue(filters[closeDateFilterId])[0];
	const closeDateCustomValue =
		closeDateFilterState &&
		'customValue' in closeDateFilterState &&
		closeDateFilterState.customValue
			? (closeDateFilterState.customValue as Record<string, string>).closeDateVal
			: undefined;
	const salesforceCloseDate = resolveSalesforceCloseDateForQuery(
		closeDateSelectedOption,
		closeDateSelectedOption === 'custom' ? closeDateCustomValue : undefined,
	);

	const isAllProductsSelected = isAllProductSearch(selectedProducts);

	const selectedFilters = {
		commonFilters: {
			range: {
				lastModified: selectedDateRange,
			},
			contributorsFilter: commonContributors,
		},
		jiraFilters: {
			issueFilter: {
				projectARIs: omitNonJiraFilterValues(
					getSelectFilterValue(filters[projectFilterId]),
					selectedProduct,
				),
				watcherARIs: omitNonJiraFilterValues(watcherARIValues, selectedProduct),
				assigneeARIs: omitNonJiraFilterValues(assigneeARIValues, selectedProduct),
				issueTypeIDs: jsmIncidentFilterIds, // fg=false branch not implemented in relay due to fetch request and dependency on intl
				issueLabels: omitNonJiraFilterValues(
					getSelectFilterValue(filters[labelFilterId]),
					selectedProduct,
				),
				statusCategories: omitNonJiraFilterValues(
					getJiraIssueStatusFilterOptions(filters[statusFilterId]),
					selectedProduct,
				),
				reporterARIs: omitNonJiraFilterValues(reporterARIValues, selectedProduct),
				commenterARIs: omitNonJiraFilterValues(commenterARIValues, selectedProduct),
				issueTypes: getSelectedJiraWorkType(selectedTypes, selectedProduct),
			},
			projectFilter: {
				projectTypes:
					experience === SearchExperience.QUICK_FIND ? [] : getJiraProjectFilter(selectedProduct),
			},
		},
		assetsFilters: {
			objectSchemaFilter: {
				schemaARIs: fg('assets_rovo_search_all')
					? getSelectFilterValue(filters[assetsSchemaFilterId])
					: [],
			},
			objectTypeFilter: {
				typeARIs: fg('assets_rovo_search_all')
					? getSelectFilterValue(filters[assetsTypeFilterId])
					: [],
			},
		},
		confluenceFilters: {
			spacesFilter: confluenceOnlyValues(selectedSpaceValues),
			isVerified: getBooleanFilterValue(filters[verifiedFilterId]) || undefined,
			contributorsFilter: getSelectFilterValue(filters[contributorFilterId]),
			ancestorIdsFilter: confluenceOnlyValues(selectedContentValues),
			range: selectedProduct !== ProductKeys.Confluence ? filterDateRange : [],
			owners: getSelectFilterValue(filters[ownerFilterId]),
			labelsFilter:
				selectedProduct === ProductKeys.Confluence
					? getSelectFilterValue(filters[labelFilterId])
					: [],
			titleMatchOnly: titleMatchValue,
			...(pageStatusValue ? { pageStatus: [pageStatusValue] } : {}),
			contentStatuses:
				selectedProduct === ProductKeys.Confluence
					? getContentStatusValue(filters[archivedFilterId])
					: [],
			containerStatus:
				selectedProduct === ProductKeys.Confluence
					? getContainerStatusValue(filters[archivedFilterId])
					: [],
		},
		mercuryFilters: {
			owners: getSelectFilterValue(filters[ownerFilterId]),
			focusAreaTypeIds: getSelectFilterValue(filters[focusAreaTypeFilterId]),
			ancestorIds: getSelectFilterValue(filters[focusAreaUnderFilterId]),
		},
		...(fg('talent_global_search')
			? {
					talentFilters: {
						owners: getSelectFilterValue(filters[ownerFilterId]),
						employmentTypes: getSelectFilterValue(filters[positionEmploymentTypeFilterId]).map(
							(value) => decodeURIComponent(value),
						),
						focusAreas: getSelectFilterValue(filters[positionFocusAreaFilterId]),
						jobFamilies: getSelectFilterValue(filters[positionJobFamilyFilterId]).map((value) =>
							decodeURIComponent(value),
						),
						statuses: getSelectFilterValue(filters[positionStatusFilterId]).map((value) =>
							decodeURIComponent(value),
						),
						teams: getSelectFilterValue(filters[positionTeamFilterId]),
					},
				}
			: {}),
		compassFilters: {
			componentFilter: {
				componentStates: getArchivedAndPendingComponentsValue(
					filters[archivedAndPendingComponentsFilterId],
				),
			},
		},
		thirdPartyFilters: getThirdPartyFilters(
			filteredProducts,
			titleMatchValue,
			selectedEntities as TypeFilterValueKey[],
			thirdPartyConfigs,
			isAllProductsSelected,
			filterDateRange,
			assignee3PValues,
			containerAris,
			createdByValues,
			lastUpdatedByValues,
			ancestorAris,
			labelsValues,
			undefined,
			selectedDatasource?.datasourceId,
			ownerValues,
			salesforceStageValues,
			salesforceCloseDate,
			opportunityStatusValue,
			pullRequestStatusValues,
			repositoryValues,
			campaignStatusValues,
			caseStatusValues,
			slackChannelValues,
			hasAttachmentValue,
			workspaceNameValues,
			reviewerValues,
			documentStatusValues,
			calendarTypeValues,
			leadStatusValues,
			taskStatusValues,
			taskTypeValues,
		),
		sortFilters,
	} satisfies SelectedFiltersType;

	const unselectedProductsThirdPartyFilters = getThirdPartyFilters(
		availableProducts,
		titleMatchValue,
		selectedEntities as TypeFilterValueKey[],
		thirdPartyConfigs,
		isAllProductsSelected,
		filterDateRange,
		undefined, // assignees
		undefined, // containerAris
		undefined, // createdBy
		undefined, // lastUpdatedBy
		undefined, // ancestorAris
		undefined, // labels
		undefined, // additionalTexts
		undefined, // selecteddatasourceid
		undefined, // ownerValues
		undefined, // salesforceCloseDate
		undefined, // opportunityStatus
		undefined, // pullRequestStatus
		undefined, // repository
		undefined, // campaignStatuses
		undefined, // caseStatuses
		undefined, // slackChannels
		undefined, // hasAttachment
		undefined, // workspaceName
		undefined, // reviewer
		undefined, // knowledgeArticlePublishStatuses
		undefined, // eventTypes
		undefined, // leadStatuses
		undefined, // taskStatuses
		undefined, // taskTypes
	);

	const {
		commonFilters,
		jiraFilters,
		confluenceFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		assetsFilters,
		talentFilters,
	} = selectedFilters;

	const isEmpty =
		!commonFilters.range.lastModified &&
		!commonFilters.contributorsFilter &&
		selectedEntities.length === 0 &&
		confluenceFilters.spacesFilter.length === 0 &&
		!confluenceFilters.isVerified &&
		!confluenceFilters.titleMatchOnly &&
		confluenceFilters.contentStatuses.length === 0 &&
		confluenceFilters.containerStatus.length === 0 &&
		confluenceFilters.contributorsFilter.length === 0 &&
		confluenceFilters.ancestorIdsFilter.length === 0 &&
		confluenceFilters.range.length === 0 &&
		confluenceFilters.owners.length === 0 &&
		confluenceFilters.labelsFilter.length === 0 &&
		(!confluenceFilters.pageStatus || confluenceFilters.pageStatus.length === 0) &&
		jiraFilters.issueFilter.assigneeARIs.length === 0 &&
		jiraFilters.issueFilter.projectARIs.length === 0 &&
		jiraFilters.issueFilter.statusCategories.length === 0 &&
		jiraFilters.issueFilter.reporterARIs.length === 0 &&
		jiraFilters.issueFilter.commenterARIs.length === 0 &&
		mercuryFilters.owners.length === 0 &&
		jiraFilters.issueFilter.issueLabels.length === 0 &&
		(!jiraFilters.issueFilter.issueTypes || jiraFilters.issueFilter.issueTypes?.length === 0) &&
		mercuryFilters.owners.length === 0 &&
		mercuryFilters.focusAreaTypeIds.length === 0 &&
		mercuryFilters.ancestorIds.length === 0 &&
		compassFilters.componentFilter.componentStates.length === 0 &&
		(fg('talent_global_search')
			? talentFilters?.owners.length === 0 &&
				talentFilters?.employmentTypes.length === 0 &&
				talentFilters?.focusAreas.length === 0 &&
				talentFilters?.jobFamilies.length === 0 &&
				talentFilters?.statuses.length === 0 &&
				talentFilters?.teams.length === 0
			: true) &&
		(fg('assets_rovo_search_all')
			? assetsFilters.objectSchemaFilter.schemaARIs.length === 0 &&
				assetsFilters.objectTypeFilter.typeARIs.length === 0
			: true) &&
		(!thirdPartyFilters.range || Object.keys(thirdPartyFilters.range).length === 0) &&
		!thirdPartyFilters.titleMatchOnly &&
		thirdPartyFilters.subtypes.length === 0 &&
		(!thirdPartyFilters.assignees || thirdPartyFilters.assignees?.length === 0) &&
		(!thirdPartyFilters.lastUpdatedBy || thirdPartyFilters.lastUpdatedBy?.length === 0) &&
		(!thirdPartyFilters.createdBy || thirdPartyFilters.createdBy?.length === 0) &&
		(!thirdPartyFilters.ancestorAris || thirdPartyFilters.ancestorAris?.length === 0) &&
		(!thirdPartyFilters.containerAris || thirdPartyFilters.containerAris?.length === 0) &&
		(!thirdPartyFilters.salesforceFilters?.stage ||
			thirdPartyFilters.salesforceFilters.stage.length === 0) &&
		(!thirdPartyFilters.salesforceFilters?.owners ||
			thirdPartyFilters.salesforceFilters.owners.length === 0) &&
		!thirdPartyFilters.salesforceFilters?.closeDate &&
		(!thirdPartyFilters.salesforceFilters?.campaignStatuses ||
			thirdPartyFilters.salesforceFilters.campaignStatuses.length === 0) &&
		(!thirdPartyFilters.salesforceFilters?.caseStatuses ||
			thirdPartyFilters.salesforceFilters.caseStatuses.length === 0) &&
		(!thirdPartyFilters.salesforceFilters?.knowledgeArticlePublishStatuses ||
			thirdPartyFilters.salesforceFilters.knowledgeArticlePublishStatuses.length === 0) &&
		(!thirdPartyFilters.gitFilters?.pullRequestStatus ||
			thirdPartyFilters.gitFilters.pullRequestStatus.length === 0) &&
		(!thirdPartyFilters.gitFilters?.repoName ||
			thirdPartyFilters.gitFilters.repoName.length === 0) &&
		!thirdPartyFilters.teamsFilters?.hasAttachment &&
		!thirdPartyFilters.thirdPartyProducts?.some(
			(p) => p.product === 'slack' && p.channels && p.channels.length > 0,
		);

	return {
		selectedFilters,
		unselectedProductsThirdPartyFilters,
		selectedEntities,
		entitiesForResultsCountQuery,
		entities: entitiesForQuery,
		hasFiltersSelected: !isEmpty,
	};
};
